import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'shared/config/store'
import {
  allAssetsEntitiesSelector,
  areAllAssetsFetchedSelector,
  getAllAssetFilteredByDateSelector,
  assetsStatusSelector,
  getFirstPageStatusSelector,
  getLastAssetCreatedSelector,
  getNextPageStatusSelector,
  selectedAssetsIdsSelector,
  systemFieldsSelector
} from 'features/assets/model/selectors/assetsSelectors'
import {
  assetsActions,
  fetchFirstRecentlyUploadedThunk,
  fetchNextRecentlyUploadedThunk,
  fetchSystemFieldsThunk,
  downloadAssetsThunk,
  createRecentlyUploadedIterator,
  loadRecentlyUploadedAssetsInBackgroundThunk
} from 'features/assets/model/slice/assetsSlice'
import { AssetsGrid } from 'features/assets/ui/AssetsGrid'
import { SkeletonAllAssets } from 'features/common/ui/SkeletonAllAssets'
import { EmptyBoardPlaceholder } from 'features/common/ui/EmptyBoardPlaceholder'
import { MultiSelectTool } from 'features/common/ui/MultiSelectTool'
import { ToolbarTop } from 'features/common/ui/dashboard/ToolbarTop'
import { useGetToolbarMainActions } from 'features/common/hooks/useGetToolbarMainActions'
import {
  boardsActions, boardsSelectors,
  downloadBoardsThunk,
  getAvailableToolbarActionsByUserRole
} from 'features/boards/model/slice/boardsSlice'
import { BoardToolbarAction, BoardAssetsToolbarAction } from 'shared/constants/toolbar'
import { getUserRole } from 'features/user/model/selectors/userSelectors'
import {
  AppstoreAddOutlined,
  ArrowRightOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MinusCircleOutlined,
  ShareAltOutlined
} from 'shared/ui'
import styled from 'styled-components'
import { IFsNodeSchema } from '@cloudike/web_files'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { normalizeDate } from 'shared/utils/time'
import { FilterButtons, FilterOption } from 'features/common/ui/FilterButtons'
import _ from 'lodash'
import { getFilesPreviewItemsSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { useScrollContainer } from 'features/common/ui/dashboard/Layout'

import {
  clearSearchState,
  fetchLabels,
  fetchSearchResults,
  fetchSearchResultsByLabel,
  fetchSearchResultsByTagsOrStatuses,
  selectItem,
  selectItemWithPressedCtrl,
  selectItemWithPressedShift,
  setIsSearchPage,
  setSearchAiTags,
  setSearchInputValue,
  setSearchTags,
  setSearchTagsType,
  setSelectedItemsIds,
  unselectAll
} from '../../features/common/model/slice/searchSlice'
import { useOnClickOutsideCards } from '../../features/common/hooks/useOnClickOutsideCards'
import {
  selectSearchAiTagsSelector,
  selectSearchAssetsSelector,
  selectSearchBoardsSelector,
  selectSearchInputValueSelector,
  selectSearchIsSearchPageSelector,
  selectSearchPrevStateSearchParamsSelector,
  selectSearchSelectedItemsIdsSelector,
  selectSearchStatusSelector,
  selectSearchTagsSelector
} from '../../features/common/model/selectors/searchSelectors'
import { IFieldOption } from '../../features/assets/model/types/assets'
import { SearchTagsType } from '../../features/common/model/types/search'
import { useGetToolbarExtraActions } from '../../features/common/hooks/useGetToolbarExtraActions'
import { ArrowsAltOutlined, LinkOutlined, Spin } from '../../shared/ui'
import { AdjustableSection } from '../../features/common/ui/dashboard'
import { Filter, getDateRange, hasAccess } from '../../shared/utils/access'
import { MemberRole } from '../../features/team/model/types/members'
import { openModal } from '../../features/common/model/slice/moadlSlice'
import { sharingActions } from '../../features/common/model/slice/sharingSlice'
import { SDK_TYPES } from '../../shared/sdk/sdkConstants'
import { filesPreviewActions, openFilesPreviewThunk } from '../../features/files-preview/model/slice/filesPreviewSlice'
import { ROUTES } from '../../shared/constants/routesPaths'
import {
  getFilteredBoardsSelector,
  selectedBoardsIdsSelector
} from '../../features/boards/model/selectors/boardsSelectors'
import { BoardsGrid } from '../../features/boards/ui/BoardsGrid'
import { LOADING_STATUSES } from '../../shared/constants/loadingStatuses'
import { DEFAULT_PAGE_SIZE } from '../../shared/constants/common'

type ContextItemType = 'board' | 'asset' | null


const FILTERS: FilterOption[] = [
  { label: 'All', value: Filter.All },
  // { label: 'Today', value: Filter.Today },
  { label: 'Last 30 days', value: Filter.Last30Days },
  // { label: 'Last 7 days', value: Filter.Last7Days },
  // { label: 'Last 6 months', value: Filter.Last6Months },
  // { label: 'This year', value: Filter.ThisYear },
  // { label: 'Last year', value: Filter.LastYear },
]

const SFilterContainer = styled.div`
  position: sticky;
  top: -36px;
  z-index: 10;
  background: white;
  padding: 16px 0;
  border-left: 4px solid white; 
  margin-left: -4px;
`

export const RecentlyUploadedPage = () => {
  const dispatch = useAppDispatch()
  const allBoards = useAppSelector(getFilteredBoardsSelector)
  const assets = useAppSelector(allAssetsEntitiesSelector)
  const recentlyUploadedAssets = useAppSelector(getAllAssetFilteredByDateSelector)
  const boards = useAppSelector(state => boardsSelectors.selectAll(state.boards))
  const selectedAssetsIds = useAppSelector(selectedAssetsIdsSelector)
  const userRole = useAppSelector(getUserRole)
  const filter = useAppSelector(state => state.assets.selectedFilter)
  const [dateFrom] = useMemo(() => getDateRange(filter), [filter])
  const abortControllerRef = useRef<AbortController | null>(null)
  const availableAssetsActions = getAvailableToolbarActionsByUserRole(userRole, true, true, false)
  const selectedAssets = assets.filter(asset => selectedAssetsIds.includes(asset.id))
  const isSearch = useAppSelector(selectSearchIsSearchPageSelector)
  const systemFields = useAppSelector(systemFieldsSelector)
  const searchAiTags = useAppSelector(selectSearchAiTagsSelector)
  const searchTags = useAppSelector(selectSearchTagsSelector)
  const searchInputValue = useAppSelector(selectSearchInputValueSelector)
  const searchStatus = useAppSelector(selectSearchStatusSelector)
  const prevStateSearchParams = useAppSelector(selectSearchPrevStateSearchParamsSelector)
  const searchSelectedItemsIds = useAppSelector(selectSearchSelectedItemsIdsSelector)
  const searchBoards = useAppSelector(selectSearchBoardsSelector)
  const searchAssets = useAppSelector(selectSearchAssetsSelector)
  const areAllAssetsFetched = useAppSelector(areAllAssetsFetchedSelector)
  const selectedBoardsIds = useAppSelector(selectedBoardsIdsSelector)
  const selectedBoards = allBoards.filter(board => selectedBoardsIds.includes(board.id))
  const status = useAppSelector(assetsStatusSelector)
  const lastDownloadedAssetCreatedDate = useAppSelector(getLastAssetCreatedSelector)
  const nextPageStatus = useAppSelector(getNextPageStatusSelector)
  const firstPageStatus = useAppSelector(getFirstPageStatusSelector)
  const isPreviewModalOpened = useAppSelector(getFilesPreviewItemsSelector).length > 0

  const [searchParams] = useSearchParams()

  const selectedAssetsTargetClassName = 'js-asset-card'
  const selectedSearchAssetsTargetClassName = 'search-asset-card'
  const selectedSearchBoardsTargetClassName = 'search-board-card'

  const navigate = useNavigate()

  const { ref: loaderBoxRef, inView: isLoaderInView } = useInView()

  useEffect(() => {
    dispatch(assetsActions.resetState())
    abortControllerRef.current = new AbortController()
  }, [])

  useEffect(() => {
    dispatch(fetchSystemFieldsThunk())
    dispatch(fetchLabels())
  },[])

  useEffect(() => {
    const createdGte = normalizeDate(dateFrom)
    const pageSize = DEFAULT_PAGE_SIZE
    const signal = abortControllerRef.current.signal
    if (!_.isNil(lastDownloadedAssetCreatedDate)) {
      // recreate iterator after switch filter
      const createdLte = normalizeDate(new Date(lastDownloadedAssetCreatedDate))
      dispatch(createRecentlyUploadedIterator({
        createdGte,
        createdLte,
        pageSize,
        signal
      }))
    } else {
      // create iterator and fetch first page if lastDownloadedAssetCreatedDate is null
      dispatch(createRecentlyUploadedIterator({
        createdGte,
        pageSize,
        signal
      })).unwrap()
        .then(() => {
          dispatch(fetchFirstRecentlyUploadedThunk())
        })
    }
  }, [lastDownloadedAssetCreatedDate, dateFrom])

  useEffect(() => {
    if (nextPageStatus !== LOADING_STATUSES.LOADING && (assets.length || recentlyUploadedAssets.length) && isLoaderInView) {
      dispatch(fetchNextRecentlyUploadedThunk())
    }
  }, [isLoaderInView, assets, recentlyUploadedAssets])

  useEffect(() => {
    const urlSearchTags = searchParams.get('searchTags')
    const urlSearchType = searchParams.get('searchType')
    const urlSearchName = searchParams.get('searchName')
    const urlSearchAiTags = searchParams.get('searchAiTags')
    const urlSearchStatus = searchParams.get('searchStatus')
    if(urlSearchTags && urlSearchType) {
      const searchQuery = JSON.parse(urlSearchTags)
      const searchType = JSON.parse(urlSearchType)
      if(searchQuery.length > 0 && searchType && systemFields.length > 0) {
        const searchTags = systemFields.find(field => field.title === 'tags').options.filter((option: IFieldOption) => searchQuery.includes(option.id))

        dispatch(setSearchTagsType(searchType))
        dispatch(setSearchTags(searchTags))
        dispatch(fetchSearchResultsByTagsOrStatuses({ tagIds: searchQuery, searchTagsType: searchType, searchType: 'tags' }))
        dispatch(setIsSearchPage(true))
      }
    } else if(urlSearchName) {
      dispatch(fetchSearchResults({ value: JSON.parse(urlSearchName) }))
      dispatch(setSearchInputValue(JSON.parse(urlSearchName)))
      dispatch(setIsSearchPage(true))
    } else if(urlSearchAiTags) {
      dispatch(fetchSearchResultsByLabel({ label: JSON.parse(urlSearchAiTags) }))
      dispatch(setSearchAiTags([{  name: JSON.parse(urlSearchAiTags) }]))
      dispatch(setIsSearchPage(true))
    } else if(urlSearchStatus) {
      dispatch(fetchSearchResultsByTagsOrStatuses({ tagIds: JSON.parse(urlSearchStatus), searchTagsType: SearchTagsType.CONTAIN, searchType: 'status' }))
      dispatch(setIsSearchPage(true))
    }
  }, [systemFields])

  useEffect(() => {
    if(
      searchTags.length === 0 &&
      searchAiTags.length === 0 &&
      searchStatus.length === 0 &&
      searchInputValue.length === 0 &&
      prevStateSearchParams.searchAiTags.length === 0 &&
      prevStateSearchParams.searchStatus.length === 0
    ) {
      const paramsToRemove = ['searchName', 'searchTags', 'searchType', 'searchStatus', 'searchAiTags']
      if (paramsToRemove.length > 0) {
        const location = window.location
        const url = new URL(location.href)
        paramsToRemove.forEach(param => {
          url.searchParams.delete(param)
        })

        navigate(url.pathname + url.search, { replace: true })
        dispatch(setIsSearchPage(false))
      }
    }
  }, [searchTags, searchAiTags, searchStatus, searchInputValue])

  // Toolbar actions
  const excludedMainAssetsActions = [
    BoardToolbarAction.Open,
    BoardToolbarAction.RemoveFromBoard,
    BoardToolbarAction.Rename,
    BoardToolbarAction.Copy,
    BoardToolbarAction.Move,
  ]

  const assetsToolbarMainActions = useGetToolbarMainActions({ availableActions: availableAssetsActions, excludedActions: excludedMainAssetsActions })

  const toolbarActionsIcons = {
    [BoardToolbarAction.Rename]: <EditOutlined />,
    [BoardToolbarAction.Copy]: <CopyOutlined />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined />,
    [BoardToolbarAction.Delete]: <DeleteOutlined />,
    [BoardToolbarAction.Download]: <DownloadOutlined />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined />,
    [BoardToolbarAction.Share]: <ShareAltOutlined />,
    [BoardToolbarAction.AddToAnotherBoard]: <AppstoreAddOutlined />,
  }

  const [withPressedCtrl, setWithPressedCtrl] = useState(false)
  const [withPressedShift, setWithPressedShift] = useState(false)
  const [isMouseSelecting, setIsMouseSelecting] = useState<boolean>(false)

  const contextMenuActionsIcons = {
    [BoardToolbarAction.Open]: <ArrowsAltOutlined />,
    [BoardToolbarAction.Share]: <LinkOutlined />,
    // [BoardToolbarAction.MakeFavorite]: <StarOutlined />,
    [BoardToolbarAction.Download]: <DownloadOutlined />,
    [BoardToolbarAction.Copy]: <CopyOutlined />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined />,
    [BoardToolbarAction.Rename]: <EditOutlined />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined />,
    [BoardToolbarAction.Delete]: <DeleteOutlined />,
  }

  const handleClickOutside = () => {
    // Пустая функция для соблюдения интерфейса
  }

  const handleContextActionClick = (
    action: BoardToolbarAction,
    itemId: string,
    items: IFsNodeSchema[],
    itemType: ContextItemType
  ) => {
    const targetItem = items.find(item => item.id === itemId)
    if (!targetItem) return

    switch (action) {
    case BoardToolbarAction.Open:
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
      break
    case BoardToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Copy:
    case BoardToolbarAction.Move:
    case BoardToolbarAction.AddToAnotherBoard:
      dispatch(openModal({ type: action, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Delete:
      dispatch(openModal({ type: BoardToolbarAction.Delete, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.RemoveFromBoard:
      dispatch(openModal({ type: BoardToolbarAction.RemoveFromBoard, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Download:
      handleDownload({ ids: [targetItem?.id], itemType: itemType })
      break
    case BoardToolbarAction.Share:
      dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      dispatch(sharingActions.setSharingAlbumConfig({
        sdkType: SDK_TYPES.FILES
      }))
      dispatch(sharingActions.setSharedItemData(targetItem))
      break
    }
  }

  const handleAssetsContextActionClick = (action: BoardAssetsToolbarAction, itemId: string) => {
    handleContextActionClick(action as unknown as BoardToolbarAction, itemId, assets, 'asset')
  }

  const handleBoardContextActionClick = (action: BoardToolbarAction, itemId: string) => {
    handleContextActionClick(action, itemId, boards, 'board')
  }

  const handleDownload = ({ ids, itemType }: { ids: string[], itemType: ContextItemType }) => {
    if(ids && ids.length) {
      if(itemType === 'board') {
        dispatch(downloadBoardsThunk({ ids }))
      } else {
        dispatch(downloadAssetsThunk({ ids }))
      }
    }
  }

  const extraActions = [
    [{
      key: BoardToolbarAction.Open,
      label: 'Open',
      icon: contextMenuActionsIcons[BoardToolbarAction.Open],
    },
    {
      key: BoardToolbarAction.Share,
      label: 'Share link',
      icon: contextMenuActionsIcons[BoardToolbarAction.Share],
    },
    {
      key: BoardToolbarAction.MakeFavorite,
      label: 'Make favorite',
      icon: contextMenuActionsIcons[BoardToolbarAction.MakeFavorite],
    }
    ],
    [{
      key: BoardAssetsToolbarAction.Download,
      label: 'Download',
      icon: contextMenuActionsIcons[BoardToolbarAction.Download],
    },
    {
      key: BoardToolbarAction.Copy,
      label: 'Copy to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Copy],
    },
    {
      key: BoardToolbarAction.Move,
      label: 'Move to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Move],
    }],
    [{
      key: BoardToolbarAction.Rename,
      label: 'Rename',
      icon: contextMenuActionsIcons[BoardToolbarAction.Rename],
    },
    {
      key: BoardToolbarAction.RemoveFromBoard,
      label: 'Remove from board',
      icon: contextMenuActionsIcons[BoardToolbarAction.RemoveFromBoard],
    },
    {
      key: BoardToolbarAction.Delete,
      label: 'Delete from library',
      icon: contextMenuActionsIcons[BoardToolbarAction.Delete],
      danger: true,
    }
    ]
  ]

  const excludedExtraAssetsActions = [
    BoardToolbarAction.Share,
    BoardToolbarAction.Download,
    BoardToolbarAction.Move,
    BoardToolbarAction.Copy,
    BoardToolbarAction.RemoveFromBoard,
    BoardToolbarAction.Delete,
    BoardToolbarAction.MakeFavorite,
  ]

  const assetsToolbarExtraActions = useGetToolbarExtraActions({ availableActions: extraActions, excludedActions: excludedExtraAssetsActions })

  const contextMenuItems = [
    {
      key: BoardAssetsToolbarAction.Open,
      label: 'Open',
      icon: <ArrowsAltOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.Share,
      label: 'Share link',
      icon: <LinkOutlined />,
    },
    // {
    //   key: BoardAssetsToolbarAction.MakeFavorite,
    //   label: 'Add to favorite',
    //   icon: <StarOutlined />,
    // },
    {
      type: 'divider' as const,
    },
    {
      key: BoardAssetsToolbarAction.Download,
      label: 'Download',
      icon: <DownloadOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.Rename,
      label: 'Rename',
      icon: <EditOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.AddToAnotherBoard,
      label: 'Add to board',
      icon: <AppstoreAddOutlined />
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      type: 'divider' as const,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      key: BoardAssetsToolbarAction.Delete,
      label: 'Delete from library',
      icon: <DeleteOutlined />,
      danger: true,
    }
  ].filter(Boolean)

  const contextMenuItemsForAssets = useCallback(() => contextMenuItems, [userRole])

  useEffect(() => {
    if (!isPreviewModalOpened) return
    
    const urlSearchParams = new URLSearchParams(window.location.search)
    const fileId = urlSearchParams.get('prv')
    
    if (fileId) {
      dispatch(loadRecentlyUploadedAssetsInBackgroundThunk({
        currentFileId: fileId,
        callback: (items, currentIndex) => {
          dispatch(filesPreviewActions.updatePreviewWithBackgroundData({
            items,
            currentIndex,
            totalItemsCount: items.length
          }))
        }
      }))
    }
  }, [dispatch, isPreviewModalOpened])

  useEffect(() => {
    return () => {
      dispatch(assetsActions.unselectAll())
    }
  }, [])

  const handleUnselectAll = useCallback(() => {
    if (isSearch) {
      dispatch(unselectAll())
    } else {
      dispatch(assetsActions.unselectAll())
    }
  }, [dispatch, isSearch])

  useOnClickOutsideCards(handleUnselectAll)

  const handleSelect = (node: IFsNodeSchema) => {
    if (isSearch) {
      if (withPressedCtrl) {
        dispatch(selectItemWithPressedCtrl({ id: node.id }))
      } else if (withPressedShift) {
        const items = node.type === 'dir' ? searchBoards : searchAssets
        dispatch(selectItemWithPressedShift({ id: node.id, items }))
      } else {
        dispatch(selectItem({ id: node.id }))
      }
    } else {
      if (withPressedCtrl) {
        dispatch(assetsActions.selectItemWithPressedCtrl({ id: node.id }))
      } else if (withPressedShift) {
        dispatch(assetsActions.selectItemWithPressedShift({ id: node.id, items: assets }))
      } else {
        dispatch(assetsActions.selectItem({ id: node.id }))
      }
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setWithPressedCtrl(true)
      }
      if (event.key === 'Shift') {
        setWithPressedShift(true)
      }
    }


    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setWithPressedCtrl(false)
      }
      if (event.key === 'Shift') {
        setWithPressedShift(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  const handleCloseToolbar = () => {
    if (isSearch) {
      dispatch(unselectAll())
    } else {
      dispatch(assetsActions.unselectAll())
    }
  }

  const openBoard = (board: IFsNodeSchema) => {
    navigate(`${ROUTES.dashboard.board}/${board.id}`)
  }

  const handleBoardDoubleClick = (board: IFsNodeSchema) => {
    openBoard(board)
  }

  useEffect(() => {
    return () => {
      if(isSearch) {
        dispatch(clearSearchState())
      }
    }
  }, [isSearch])

  const handleToolbarActionClick = (
    itemType: ContextItemType,
    actionId: BoardToolbarAction,
    items: any[]
  ) => {
    if (isSearch) {
      const selectedSearchAssets = searchAssets?.filter(asset => searchSelectedItemsIds.includes(asset.id)) || []
      const selectedSearchBoards = searchBoards.filter(board => searchSelectedItemsIds.includes(board.id))

      switch (actionId) {
      case BoardToolbarAction.Open:
        if(items.length === 1) {
          const asset = items[0] as IFsNodeSchema
          if (asset.type === 'file' || asset.type as string === 'link') {
            dispatch(openFilesPreviewThunk({ items: searchAssets as any, currentItemId: asset.id, totalItemsCount: 1, navigate }))
          } else if (asset.type === 'dir') {
            navigate(`${ROUTES.dashboard.board}/${asset.id}`)
          }
        }
        dispatch(unselectAll())
        break
      case BoardToolbarAction.Rename:
        dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [items[0].id], items: [items[0]] } }))
        break
      case BoardToolbarAction.Copy:
      case BoardToolbarAction.Move:
      case BoardToolbarAction.AddToAnotherBoard:
        dispatch(openModal({
          type: actionId,
          context: itemType,
          data: {
            ids: searchSelectedItemsIds,
            items: [...selectedSearchAssets, ...selectedSearchBoards]
          }
        }))
        break
      case BoardToolbarAction.Delete:
        dispatch(openModal({
          type: BoardToolbarAction.Delete,
          context: itemType,
          data: {
            ids: searchSelectedItemsIds,
            items: [...selectedSearchAssets, ...selectedSearchBoards]
          }
        }))
        break
      case BoardToolbarAction.Download:
        const assetIds = selectedSearchAssets.map(asset => asset.id)
        const boardIds = selectedSearchBoards.map(board => board.id)
        if (boardIds.length > 0 || assetIds.length > 0) {
          handleDownload({ ids: [...boardIds, ...assetIds], itemType: itemType })
        }
        break
      case BoardToolbarAction.Share:
        if (selectedSearchAssets.length === 1) {
          dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [selectedSearchAssets[0].id], items: [selectedSearchAssets[0]] } }))
          dispatch(sharingActions.setSharingAlbumConfig({
            sdkType: SDK_TYPES.FILES
          }))
          dispatch(sharingActions.setSharedItemData(selectedSearchAssets[0]))
        } else if (selectedSearchBoards.length === 1) {
          dispatch(openModal({ type: BoardToolbarAction.Share, context: 'board', data: { ids: [selectedSearchBoards[0].id], items: [selectedSearchBoards[0]] } }))
          dispatch(sharingActions.setSharingAlbumConfig({
            sdkType: SDK_TYPES.FILES
          }))
          dispatch(sharingActions.setSharedItemData(selectedSearchBoards[0]))
        }
        break
      }
    } else {
      switch (actionId) {
      case BoardToolbarAction.Open:
        if(items.length === 1) {
          const asset = items[0] as IFsNodeSchema
          dispatch(openFilesPreviewThunk({ items: assets as any, currentItemId: asset.id, totalItemsCount: 1, navigate }))
        }
        dispatch(assetsActions.unselectAll())
        dispatch(boardsActions.unselectAll())
        break
      case BoardToolbarAction.Rename:
        dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [items[0].id], items: [items[0]] } }))
        break
      case BoardToolbarAction.Copy:
      case BoardToolbarAction.Move:
      case BoardToolbarAction.AddToAnotherBoard:
        dispatch(openModal({ type: actionId, context: itemType, data: { ids: [...selectedAssetsIds, ...selectedBoardsIds], items: [...selectedAssets, ...selectedBoards] } }))
        break
      case BoardToolbarAction.Delete:
        dispatch(openModal({ type: BoardToolbarAction.Delete, context: itemType, data: { ids: [...selectedAssetsIds, ...selectedBoardsIds], items: [...selectedAssets, ...selectedBoards] } }))
        break
      case BoardToolbarAction.Download:
        if (selectedBoardsIds.length > 0 || selectedAssetsIds.length > 0) {
          handleDownload({ ids: [...selectedBoardsIds, ...selectedAssetsIds], itemType: itemType })
        }
        break
      case BoardToolbarAction.Share:
        dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [selectedAssetsIds[0]], items: [selectedAssets[0]] } }))
        dispatch(sharingActions.setSharingAlbumConfig({
          sdkType: SDK_TYPES.FILES
        }))
        dispatch(sharingActions.setSharedItemData(selectedAssets[0]))
        break
      }
    }
  }

  const handleMouseSelect = (event: any, result: { id: string, targetClassName: string }[]) => {
    const selectedItems = result.map((item) => {
      if (item.targetClassName === selectedSearchBoardsTargetClassName) {
        const board = searchBoards.find((board) => board.id === item.id)
        return {
          ...board,
          itemType: 'board',
        }
      } else if (item.targetClassName === selectedAssetsTargetClassName) {
        const asset = assets.find((asset) => asset.id === item.id)
        return {
          ...asset,
          itemType: 'asset',
        }
      } else if (item.targetClassName === selectedSearchAssetsTargetClassName) {
        const searchAsset = searchAssets.find((asset) => asset.id === item.id)
        return {
          ...searchAsset,
          itemType: 'asset',
        }
      }
    })
    const selectedAssets = selectedItems.filter((item) => item?.itemType === 'asset')
    const selectedBoards = selectedItems.filter((item) => item?.itemType === 'board')

    if (isSearch) {
      const selectedIds = [...selectedAssets, ...selectedBoards].map(item => item?.id).filter(id => !!id)
      if (selectedIds.length) {
        dispatch(setSelectedItemsIds(selectedIds))
      }
    } else {
      if (selectedAssets.length || selectedBoards.length) {
        const selectedAssetsIds = selectedAssets.map((asset) => asset?.id).filter(id => !!id)
        const selectedBoardsIds = selectedBoards.map((board) => board?.id).filter(id => !!id)
        dispatch(boardsActions.setSelectedItemsIds(selectedBoardsIds))
        dispatch(assetsActions.setSelectedItemsIds(selectedAssetsIds))
      }
    }
  }

  const handleMouseSelectEnd = () => {
    setIsMouseSelecting(false)
  }

  const handleMouseSelectStart = () => {
    setIsMouseSelecting(true)
  }

  const handleChangeFilter = (filter: string) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for the new request
    const newAbortController = new AbortController()
    abortControllerRef.current = newAbortController

    dispatch(assetsActions.setSelectedFilter(filter))
  }

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [abortControllerRef])

  const scrollContainerRef = useScrollContainer()

  return (
    <div>
      {assets.length > 0 && (
        <SFilterContainer>
          <FilterButtons
            filters={FILTERS}
            activeFilter={filter}
            onFilterChange={handleChangeFilter}
          />
        </SFilterContainer>
      )}

      {status === LOADING_STATUSES.LOADING || firstPageStatus === LOADING_STATUSES.LOADING ? (<SkeletonAllAssets />) : (
        <>
          {isSearch ? (
            <>
              <ToolbarTop
                isShown={searchSelectedItemsIds.length > 0}
                itemsType={searchSelectedItemsIds.some(id =>
                  searchBoards.some(board => board.id === id)) ? 'board' : 'asset'}
                mainActions={assetsToolbarMainActions}
                extraActionsGroups={assetsToolbarExtraActions}
                selectedItems={[
                  ...searchBoards.filter(board => searchSelectedItemsIds.includes(board.id)),
                  ...searchAssets?.filter(asset => searchSelectedItemsIds.includes(asset.id)) || []
                ]}
                actionsIcons={toolbarActionsIcons}
                onActionClick={handleToolbarActionClick}
                onClose={handleCloseToolbar}
                multiselectSupportedActions={[
                  BoardToolbarAction.Move,
                  BoardToolbarAction.Copy,
                  BoardToolbarAction.Delete,
                  BoardToolbarAction.Download,
                  BoardToolbarAction.AddToAnotherBoard,
                ]}
              />

              {searchBoards.length > 0 && (
                <AdjustableSection
                  title={searchAssets.length === 0 ? "" : "Boards"}
                >
                  <BoardsGrid
                    boards={searchBoards as IFsNodeSchema[]}
                    onSelect={handleSelect}
                    selectedItemsIds={searchSelectedItemsIds}
                    onClickOutside={handleClickOutside}
                    onBoardDoubleClick={handleBoardDoubleClick}
                    onMenuItemClick={handleBoardContextActionClick}
                    contextMenuItems={contextMenuItems}
                    selectedTargetClassName={selectedSearchBoardsTargetClassName}
                    onContextStateChanged={() => false}
                    isOuterContextMenuOpened={false}
                  />
                </AdjustableSection>
              )}

              {searchAssets.length > 0 && (
                <SAdjustableSectionAssets
                  title={searchBoards.length === 0 ? "" : "Assets"}
                >
                  <AssetsGrid
                    assets={searchAssets as any}
                    onSelect={handleSelect}
                    selectedItemsIds={searchSelectedItemsIds}
                    onClickOutside={handleClickOutside}
                    onMenuItemClick={handleAssetsContextActionClick}
                    contextMenuItems={contextMenuItemsForAssets}
                    isItemHoverEnabled={!isMouseSelecting}
                    selectedTargetClassName={selectedSearchAssetsTargetClassName}
                    onDoubleClick={handleUnselectAll}
                    onContextStateChanged={() => false}
                    isOuterContextMenuOpened={false}
                  />
                </SAdjustableSectionAssets>
              )}

              {
                searchAssets.length === 0 && searchBoards.length === 0 && (
                  <EmptyBoardPlaceholder
                    texts={{
                      title: 'Nothing found',
                      description: 'Please check for typos or filters, or try a different search query.'
                    }}
                  />
                )
              }
            </>
          ) : (
            <>
              <ToolbarTop
                isShown={selectedAssetsIds.length > 0}
                itemsType="asset"
                mainActions={assetsToolbarMainActions}
                extraActionsGroups={assetsToolbarExtraActions}
                selectedItems={selectedAssets}
                actionsIcons={toolbarActionsIcons}
                onActionClick={handleToolbarActionClick}
                onClose={handleCloseToolbar}
                multiselectSupportedActions={[
                  BoardToolbarAction.Move,
                  BoardToolbarAction.Copy,
                  BoardToolbarAction.Delete,
                  BoardToolbarAction.Download,
                  BoardToolbarAction.AddToAnotherBoard,
                ]}
              />

              {recentlyUploadedAssets.length > 0 && (
                <SAdjustableSectionAssets>
                  <AssetsGrid
                    assets={recentlyUploadedAssets}
                    onSelect={handleSelect}
                    onClickOutside={handleClickOutside}
                    onMenuItemClick={handleAssetsContextActionClick}
                    selectedItemsIds={selectedAssetsIds}
                      contextMenuItems={contextMenuItemsForAssets}
                    isItemHoverEnabled={!isMouseSelecting}
                    selectedTargetClassName={selectedAssetsTargetClassName}
                    onDoubleClick={handleUnselectAll}
                    onContextStateChanged={() => false}
                    isOuterContextMenuOpened={false}
                  />
                </SAdjustableSectionAssets>
              )}

              {!areAllAssetsFetched && !!assets.length && (
                <SLoaderRow ref={loaderBoxRef}>
                  {nextPageStatus === LOADING_STATUSES.LOADING ? <SSpinner /> : null}
                </SLoaderRow>
              )}

              {
                assets.length === 0 && (
                  <EmptyBoardPlaceholder
                    texts={{
                      title: 'Nothing here yet',
                      description: 'Assets you upload will appear here'
                    }}
                  />
                )
              }
            </>
          )}
        </>
      )}

      <MultiSelectTool
        onSelect={handleMouseSelect}
        onSelectStart={handleMouseSelectStart}
        onSelectEnd={handleMouseSelectEnd}
        selectedTargetClassNames={[
          selectedAssetsTargetClassName,
          selectedSearchAssetsTargetClassName,
          selectedSearchBoardsTargetClassName,
        ]}
        scrollRef={scrollContainerRef}
      />
    </div>
  )
}

const SLoaderRow = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 40px;
`

const SSpinner = styled(Spin)``

const SAdjustableSectionAssets = styled(AdjustableSection)`
  margin-top: 20px;
`

